import React, { useMemo } from 'react';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';

import { AISmartButton } from '@atlassian/ai-smart-button';
import { isFedRamp } from '@atlassian/atl-context';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useConversationAssistantConfig } from '@confluence/smart-create/entry-points/useConfig.ts';
import {
	useRovoConfigGetActions,
	useRovoConfigTags,
	useRovoConfigCustomGreeting,
	useRovoConfigShouldShowGreeting,
	useRovoConfigPrioritizeCustomActionResults,
} from '@confluence/rovo-config-provider-common/entry-points/useRovoCurrentConfig.ts';

import { useCreateRovoExperienceSidebarController } from './hooks/useCreateRovoExperienceSidebarController.ts';

const styles = cssMap({
	smartButtonWrapperStyles: {
		position: 'absolute',
		zIndex: layers.modal(),
		right: 0,
		bottom: 0,
	},
});

type AISmartButtonProps = {
	contentId: string;
	contentType: string;
	pageMode: string;
	isDisabled?: boolean;
};

export function AISmartButtonWrapper({
	contentId,
	contentType,
	pageMode,
	isDisabled = false,
}: AISmartButtonProps) {
	const { cloudId, orgId } = useSessionData();
	const isNav4Enabled = useIsNav4Enabled();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const sidebarApi = useCreateRovoExperienceSidebarController();
	const getActions = useRovoConfigGetActions();
	const tags = useRovoConfigTags();
	const customGreeting = useRovoConfigCustomGreeting();
	const shouldShowGreeting = useRovoConfigShouldShowGreeting();
	const prioritizeCustomActionResults = useRovoConfigPrioritizeCustomActionResults();

	// Gate ON: environment-aware enablement via feature encoding; Gate OFF: legacy FedRAMP fallback
	const isAIEnvEnabled = fg('use_frontend_feature_encoding_sdk')
		? isFeatureEnabled('confluence', 'confluence-ai')
		: !isFedRamp();
	const isEnabled = isAIEnvEnabled && isAIEnabledForContent && isRovoEnabledForContent;

	// 48px = nav header height, 8px = object sidebar bottom padding, 16px = AI smart button bottom margin, 8px = extra margin so it doesn't align with nav header
	const navbarOffset = 48 + 8 + 16 + 8;
	// From confluence/next/packages/content-types-header/src/ContentTypesHeader.tsx
	const OBJECT_HEADER_CONTAINER_HEIGHT = 56;
	// From platform/packages/editor/editor-shared-styles/src/consts/consts.ts
	const FULL_PAGE_EDITOR_TOOLBAR_HEIGHT = 40;

	// Calculate total vertical offset based on the maximum header height (occurs in edit mode)
	const totalOffset =
		FULL_PAGE_EDITOR_TOOLBAR_HEIGHT + OBJECT_HEADER_CONTAINER_HEIGHT + navbarOffset;

	// Memoize analytics attributes to prevent unnecessary re-renders
	const analyticsAttributes = useMemo(
		() => ({
			pageMode,
		}),
		[pageMode],
	);

	const uiConfig = useMemo(
		() => ({
			...(customGreeting ? { customGreeting } : {}),
			...(shouldShowGreeting !== undefined
				? {
						elements: {
							shouldShowGreeting,
						},
					}
				: {}),
		}),
		[customGreeting, shouldShowGreeting],
	);

	const overrideMiniModalWrapper = useMemo(
		() => (children: React.ReactNode) => {
			return <Box xcss={styles.smartButtonWrapperStyles}>{children}</Box>;
		},
		[],
	);
	const conversationAssistantConfig = useConversationAssistantConfig({
		assistanceServiceConfigHeaders: {
			'x-issynthetic-traffic': 'false',
		},
		preventAbortRequestsOnStagingModalClose: true,
		preventAbortRequestsOnStandaloneChatClose: true,
	});

	if (!isEnabled) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.AI_MATE}>
			<AISmartButton
				product="confluence"
				cloudId={cloudId}
				orgId={orgId}
				isNav4Enabled={isNav4Enabled}
				sidebarApi={sidebarApi}
				productPage={contentType}
				analyticsAttributes={analyticsAttributes}
				isDisabled={isDisabled}
				topOffset={totalOffset}
				conversationAssistantConfig={conversationAssistantConfig}
				overrideMiniModalWrapper={overrideMiniModalWrapper}
				getActions={getActions}
				prioritizeCustomActionResults={prioritizeCustomActionResults}
				tags={tags}
				uiConfig={uiConfig}
			/>
		</ErrorBoundary>
	);
}
